import React, { useState, useEffect } from "react";
import { FaCheck } from "react-icons/fa";
import { SimpleSelect } from "./common/SimpleSelect";
import { getYear } from "date-fns";
import _ from "lodash";
import { getMonthAbbreviation } from "../services/dateService";
import { QuestionnaireHeader } from "./QuestionnaireHeader";
import { QuestionnaireFooter } from "./QuestionnaireFooter";

export const QuestionnaireConditionalDatePage = ({
	def,
	onRespond,
	currentResponse,
}) => {
	const [selectedOption, setSelectedOption] = useState({
		option: "",
		date: 0,
	});
	const [dateDisabled, setDateDisabled] = useState(true);
	const [currentMonthOption, setCurrentMonthOption] = useState();
	const [currentYearOption, setCurrentYearOption] = useState();

	const years = _.range(1950, getYear(Date.now()) + 1);
	const [yearsOptions] = useState(
		years.map((y) => {
			return {
				id: y,
				label: y,
				value: y,
			};
		})
	);

	const months = _.range(12);
	const [monthsOptions] = useState(
		months.map((m) => {
			return {
				id: m,
				label: getMonthAbbreviation(m),
				value: m,
			};
		})
	);

	const onChangeMonth = (sel) => {
		setCurrentMonthOption(sel);

		const date = new Date(selectedOption.date);
		date.setMonth(sel.value);

		const _selectedOption = { ...selectedOption };
		_selectedOption.date = date.valueOf();
		setSelectedOption(_selectedOption);

		const response = {
			id: def.id,
			value: _selectedOption,
		};
		onRespond(response);
	};

	const onChangeYear = (sel) => {
		setCurrentYearOption(sel);

		const date = new Date(selectedOption.date);
		date.setFullYear(sel.value);

		const _selectedOption = { ...selectedOption };
		_selectedOption.date = date.valueOf();
		setSelectedOption(_selectedOption);

		const response = {
			id: def.id,
			value: _selectedOption,
		};
		onRespond(response);
	};

	useEffect(() => {
		setSelectedOption({ option: "", date: 0 });
	}, [def]);

	useEffect(() => {
		const processDate = (dt) => {
			const ret = {
				mnth: null,
				year: null,
			};

			if (dt) {
				const date = new Date(dt);
				const m = date.getMonth();
				ret.mnth = monthsOptions.find((month) => month.value === m);
				const y = date.getFullYear();
				ret.year = yearsOptions.find((year) => year.value === y);
			}

			return ret;
		};

		if (currentResponse && currentResponse.value) {
			const val = currentResponse.value;
			setSelectedOption(val);

			setDateDisabled(val.option.toString() !== "3");
			if (val.option.toString() === "3") {
				const dt = processDate(val.date);

				setCurrentMonthOption(dt.mnth);
				setCurrentYearOption(dt.year);
			} else {
				setCurrentMonthOption();
				setCurrentYearOption();
			}
		} else if (def.answer) {
			const val = def.answer;
			setSelectedOption(val);

			setDateDisabled(val.option.toString() !== "3");
			if (val.option.toString() === "3") {
				const dt = processDate(val.date);

				setCurrentMonthOption(dt.mnth);
				setCurrentYearOption(dt.year);
			} else {
				setCurrentMonthOption();
				setCurrentYearOption();
			}

			const response = {
				id: def.id,
				value: val,
			};
			onRespond(response);
		} else {
			setSelectedOption({ option: "", date: 0 });
			setCurrentMonthOption();
			setCurrentYearOption();
		}
	}, [currentResponse, yearsOptions, monthsOptions, def, onRespond]);

	const onChangeOption = (e) => {
		const value = e.target.value;
		setDateDisabled(value !== "3");

		const _selectedOption = { ...selectedOption };
		_selectedOption.option = value;

		if (value === "3") {
			const date = new Date();
			date.setHours(0, 0, 0, 0);
			date.setDate(14); // put the date firmly in the middle of the month
			date.setMonth(date.getMonth() - 1); // and make it last month by default
			_selectedOption.date = date.valueOf();
		} else {
			_selectedOption.date = 0;
		}

		const response = {
			id: def.id,
			value: _selectedOption,
		};

		setSelectedOption(_selectedOption);
		onRespond(response);
	};

	const renderOptions = (s) => {
		return def.options
			? def.options.map((option, i) => (
					<label
						key={`${i}_label`}
						className={`${
							s === option.value.toString()
								? `bg-black text-white font-medium`
								: "bg-gray-100 text-black"
						} text-right text-base sm:text-lg leading-5 sm:leading-6 font-normal rounded-full mx-3 option pr-2 pl-7 py-2.5 cursor-pointer flex items-center justify-end my-3 lg:my-4`}
					>
						{option.label}
						<span
							className={`flex flex-0-0-auto justify-center items-center w-4 h-4 text-white border border-gray-400 rounded mx-2.5`}
						>
							{s === option.value.toString() && <FaCheck size={8} />}
						</span>
						<input
							key={`${i}_input`}
							name={def.title}
							value={option.value.toString()}
							className={"hidden "}
							type="radio"
							checked={s === option.value.toString()}
							onChange={onChangeOption}
						/>
					</label>
			  ))
			: null;
	};

	return (
		<>
			<QuestionnaireHeader def={def} />

			<div>{renderOptions(selectedOption.option)}</div>

			<div className={"w-full mx-auto overflow-y-visible h-fit px-6 mt-4"}>
				<div className={"mb-2 w-full text-sm"}>
					<label className={"block w-full my-auto"} htmlFor={"date"}>
						Date
					</label>
					<div className="flex-grow  flex">
						<div className="w-full mr-2">
							<SimpleSelect
								disabled={dateDisabled}
								options={monthsOptions}
								value={currentMonthOption}
								onChange={onChangeMonth}
								placeholder="Month"
								isSearchable={false}
							/>
						</div>
						<div className="w-full">
							<SimpleSelect
								disabled={dateDisabled}
								options={yearsOptions}
								value={currentYearOption}
								onChange={onChangeYear}
								placeholder="Year"
								isSearchable={false}
							/>
						</div>
					</div>
				</div>
			</div>

			<QuestionnaireFooter def={def} />
		</>
	);
};
